import moment from 'moment';
import {
  unwrapResponse,
  normalizeField,
  objectToCamelCase,
  ensureArray,
  parseInteger,
} from '@/services/base';
import { normalizeCustomField as normalizeWorkkardField } from '@/common/normalizers/workkardFields';

const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY, hh:mm A';

const toDisplayDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    // Unix seconds coming from the /1.0/activity/list response.
    return moment.unix(value).format(DISPLAY_DATE_FORMAT);
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : String(value);
};

const normalizeActivityFile = (raw = {}) => {
  if (!raw || typeof raw !== 'object') return null;
  const camelCased = objectToCamelCase(raw);
  return {
    guid: normalizeField(camelCased, ['guid'], ''),
    caption: normalizeField(camelCased, ['caption'], ''),
    url: normalizeField(camelCased, ['url'], ''),
    uploadedDate: normalizeField(camelCased, ['uploadedDate'], null),
  };
};

const normalizeCustomField = (raw = {}) => {
  if (!raw || typeof raw !== 'object') return null;

  return {
    fieldId: normalizeField(raw, ['fieldId', 'FieldId'], null),
    fieldName: normalizeField(raw, ['fieldName', 'FieldName'], ''),
    fieldValue: normalizeField(raw, ['fieldValue', 'FieldValue', 'value', 'Value'], ''),
    fieldType: normalizeField(raw, ['fieldType', 'FieldType'], ''),
  };
};

const normalizeWorkkardInfo = (raw) =>
  ensureArray(raw)
    .map(normalizeWorkkardField)
    .filter(Boolean)
    .sort((a, b) => a.Sequence - b.Sequence);

export const normalizeActivityListItem = (raw = {}) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const rawCustomFields = raw.CustomFields || raw.customFields || [];
  const rawWorkkardInfo = raw.workkard_info || raw.WorkkardInfo || raw.workkardInfo || [];
  const rawActivityFiles = raw.activity_files || raw.ActivityFiles || raw.activityFiles || [];

  const camelCased = objectToCamelCase(raw);

  const workkardId = normalizeField(camelCased, ['workkardId'], null);
  const dueDateRaw = normalizeField(camelCased, ['dueDate'], null);
  const createdDateRaw = normalizeField(camelCased, ['createdDate'], null);
  const closedDateRaw = normalizeField(camelCased, ['closedDate'], null);
  const completedDateRaw = normalizeField(camelCased, ['completedDate'], null);

  return {
    activityId: normalizeField(camelCased, ['activityId'], null),
    workkardId,
    workkardNumber: normalizeField(
      camelCased,
      ['workkardNoFull', 'workkardNumber', 'workkardNo'],
      workkardId != null ? String(workkardId) : ''
    ),
    workkardNo: normalizeField(camelCased, ['workkardNo'], ''),
    workkardTitle: normalizeField(camelCased, ['workkardTitle'], ''),
    workkardTemplateId: normalizeField(camelCased, ['workkardTemplateId'], null),
    templateId: normalizeField(camelCased, ['templateId'], null),
    activityName: normalizeField(camelCased, ['activityName', 'activityTitle', 'title'], ''),
    activityDescription: normalizeField(camelCased, ['activityDescription', 'description'], ''),
    activityTemplateType: normalizeField(camelCased, ['activityTemplateType'], ''),
    status: normalizeField(camelCased, ['status'], ''),
    resolutionForDisplay: normalizeField(camelCased, ['resolutionForDisplay', 'resolution'], ''),
    skillSet: normalizeField(camelCased, ['skillset', 'skillSet'], ''),
    progress: normalizeField(camelCased, ['progress'], 0),
    dueDate: dueDateRaw,
    dueOn: normalizeField(
      camelCased,
      ['dueDateString', 'dueOn'],
      toDisplayDate(dueDateRaw)
    ),
    dueDateTz: normalizeField(camelCased, ['dueDateTz'], ''),
    createdDate: normalizeField(
      camelCased,
      ['createdDateString'],
      toDisplayDate(createdDateRaw)
    ),
    createdDateRaw,
    closedDate: normalizeField(
      camelCased,
      ['closedDateString'],
      toDisplayDate(closedDateRaw)
    ),
    completedDate: toDisplayDate(completedDateRaw),
    activityOwnerId: normalizeField(camelCased, ['activityOwnerId'], null),
    activityAssigneeId: normalizeField(camelCased, ['activityAssigneeId'], null),
    assigneeOrgId: normalizeField(camelCased, ['assigneeOrgId'], null),
    ownerOrgId: normalizeField(camelCased, ['ownerOrgId'], null),
    activityAssignee: normalizeField(camelCased, ['activityAssignee'], ''),
    activityOwnerName: normalizeField(camelCased, ['activityOwnerName'], ''),
    activityOwnerImage: normalizeField(camelCased, ['activityOwnerImage'], ''),
    activityAssigneeImage: normalizeField(camelCased, ['activityAssigneeImage'], ''),
    isAssigneeSysUser: normalizeField(camelCased, ['isAssigneeSysUser'], false),
    isAssigneeDeleted: normalizeField(camelCased, ['isAssigneeDeleted'], false),
    isAssigneeOrganizationDeleted: normalizeField(camelCased, ['isAssigneeOrganizationDeleted'], false),
    accessToOrgIds: normalizeField(camelCased, ['accessToOrgIds'], []),
    statusColor: normalizeField(camelCased, ['statusColor'], ''),
    activityFiles: ensureArray(rawActivityFiles)
      .map(normalizeActivityFile)
      .filter(Boolean),
    customFields: ensureArray(rawCustomFields)
      .map(field => normalizeCustomField(objectToCamelCase(field)))
      .filter(Boolean),
    workkardInfo: normalizeWorkkardInfo(rawWorkkardInfo),
  };
};

export const normalizeTabCounts = (raw = {}) => {
  if (!raw || typeof raw !== 'object') {
    return {
      openCount: 0,
      closeCount: 0,
      unassignedCount: 0,
      watchingCount: 0,
    };
  }

  const camelCased = objectToCamelCase(raw);

  return {
    openCount: parseInteger(normalizeField(camelCased, ['openCount'], 0), 0),
    closeCount: parseInteger(normalizeField(camelCased, ['closeCount'], 0), 0),
    unassignedCount: parseInteger(normalizeField(camelCased, ['unassignCount', 'unassignedCount'], 0), 0),
    watchingCount: parseInteger(normalizeField(camelCased, ['watchingCount'], 0), 0),
  };
};

const validateListingResponse = (data) => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid activity listing response: expected object');
  }
};

const validateTabCountResponse = (data) => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid tab count response: expected object');
  }
};

export const normalizeListingResponse = (response, isScroll = false) => {
  if (!response) {
    return {
      activitiesFullData: [],
      total: 0,
      skip: 0,
      take: 0,
      page: 1,
      status: '',
      sortDir: 'DESC',
      sortBy: 1,
      tabCounts: normalizeTabCounts({}),
    };
  }

  const unwrapped = unwrapResponse(response);

  validateListingResponse(unwrapped);

  const listData = ensureArray(normalizeField(unwrapped, ['activities', 'Activities'], []));

  return {
    activitiesFullData: listData.map(normalizeActivityListItem).filter(Boolean),
    total: parseInteger(normalizeField(unwrapped, ['total', 'Total'], 0), 0),
    skip: parseInteger(normalizeField(unwrapped, ['skip', 'Skip'], 0), 0),
    take: parseInteger(normalizeField(unwrapped, ['take', 'Take'], 0), 0),
    page: parseInteger(normalizeField(unwrapped, ['page', 'Page'], 1), 1),
    status: normalizeField(unwrapped, ['status', 'Status'], ''),
    sortDir: normalizeField(unwrapped, ['sortDir', 'SortDir'], 'DESC'),
    sortBy: parseInteger(normalizeField(unwrapped, ['sortBy', 'SortBy'], 1), 1),
    // ListActivities now returns open/close/unassigned counts alongside the
    // page of activities (mirrors WorkkardListing's dedicated tab-count
    // response), so every successful list fetch can refresh all three tab
    // counts instead of only the one matching the status just fetched.
    tabCounts: normalizeTabCounts(unwrapped),
  };
};

export const normalizeTabCountResponse = (response) => {
  if (!response) {
    return {
      tabCounts: normalizeTabCounts({}),
      sessionContext: null,
    };
  }

  const unwrapped = unwrapResponse(response);
  
  validateTabCountResponse(unwrapped);
  
  const tabCounts = normalizeTabCounts(unwrapped);

  return {
    tabCounts,
    sessionContext: normalizeField(unwrapped, ['sessionContext'], null),
  };
};
